import { msg } from "@lingui/core/macro";
import { createEffect, type JSX, Show } from "solid-js";
import { AlertTriangle, RefreshCw, RotateCcw } from "@fleet/icons";
import { cn } from "@bso/shared";
import { Button } from "@/components/ui/button";
import { GlassSurface } from "@fleet/ui";
import { useI18n } from "@/providers/i18n-provider";

interface ErrorFallbackProps {
  /** The thrown error (any value Solid caught). */
  error: unknown;
  /** Re-runs the boundary's children. */
  reset: () => void;
  /** Optional custom title/description for region-scoped boundaries. */
  title?: string;
  description?: string;
  /** "page" fills the viewport; "region" fits inside a panel/section. */
  variant?: "page" | "region";
  /** Tag used in the console log to locate which boundary fired. */
  scope?: string;
  class?: string;
}

/**
 * Theme-aware inline SVG for the error state. Decorative (aria-hidden); the
 * surrounding card always carries the real heading + description. Uses
 * `currentColor`/`text-destructive` so every theme renders consistently, and
 * the gentle float honors `prefers-reduced-motion` via the global CSS guard.
 */
function ErrorSvg(): JSX.Element {
  return (
    <svg
      width="112"
      height="112"
      viewBox="0 0 112 112"
      fill="none"
      aria-hidden="true"
      role="presentation"
      class="motion-safe:animate-float"
    >
      {/* Open book base */}
      <path
        d="M16 34c14-7 26-7 40 0v54c-14-7-26-7-40 0V34z"
        class="fill-muted/40 stroke-border"
        stroke-width="2.5"
        stroke-linejoin="round"
      />
      <path
        d="M96 34c-14-7-26-7-40 0v54c14-7 26-7 40 0V34z"
        class="fill-muted/30 stroke-border"
        stroke-width="2.5"
        stroke-linejoin="round"
      />
      <line
        x1="56"
        y1="34"
        x2="56"
        y2="88"
        class="stroke-border"
        stroke-width="2"
        stroke-linecap="round"
      />
      {/* Alert badge */}
      <circle cx="56" cy="44" r="20" class="fill-card stroke-destructive" stroke-width="3" />
      <line
        x1="56"
        y1="36"
        x2="56"
        y2="48"
        class="stroke-destructive"
        stroke-width="3.5"
        stroke-linecap="round"
      />
      <circle cx="56" cy="55" r="2.2" class="fill-destructive" />
    </svg>
  );
}

/**
 * Premium fallback rendered inside Solid `<ErrorBoundary>`. Never a blank
 * screen: shows an illustration + title + description + "Retry" (reset) and
 * "Reload" (full reload). Logs the error to the console for diagnostics.
 */
export function ErrorFallback(props: ErrorFallbackProps): JSX.Element {
  const { t } = useI18n();

  createEffect(() => {
    console.error(
      `[ErrorBoundary${props.scope ? `:${props.scope}` : ""}] Caught error:`,
      props.error,
    );
  });

  const message = () => {
    if (props.error instanceof Error && props.error.message) {
      return props.error.message;
    }
    return undefined;
  };

  const isRegion = () => props.variant === "region";

  return (
    <div
      role="alert"
      class={cn(
        "flex items-center justify-center p-4",
        isRegion() ? "h-full w-full min-h-[12rem]" : "min-h-dvh bg-background",
        props.class,
      )}
    >
      <GlassSurface
        class={cn(
          "w-full flex flex-col items-center text-center motion-safe:animate-fade-in",
          isRegion() ? "max-w-sm p-6 space-y-4" : "max-w-md p-8 space-y-6",
        )}
      >
        <div class="text-destructive/80">
          <Show when={!isRegion()} fallback={<AlertTriangle class="h-10 w-10" />}>
            <ErrorSvg />
          </Show>
        </div>
        <div class="space-y-2">
          <h2 class={cn("font-bold tracking-tight", isRegion() ? "text-lg" : "text-2xl")}>
            {props.title ?? t(msg`Something went wrong`)}
          </h2>
          <p class="text-muted-foreground text-sm">
            {props.description ??
              t(
                msg`An unexpected error interrupted this view. You can try again or reload the app.`,
              )}
          </p>
          <Show when={message()}>
            {(m) => (
              <p class="text-2xs font-mono text-muted-foreground/70 break-words max-w-full pt-1">
                {m()}
              </p>
            )}
          </Show>
        </div>
        <div class="flex gap-3 w-full">
          <Button
            variant="outline"
            class="flex-1 gap-2"
            onClick={() => {
              globalThis.location.reload();
            }}
          >
            <RotateCcw class="h-4 w-4" />
            {t(msg`Reload`)}
          </Button>
          <Button
            class="flex-1 gap-2"
            onClick={() => {
              props.reset();
            }}
          >
            <RefreshCw class="h-4 w-4" />
            {t(msg`Retry`)}
          </Button>
        </div>
      </GlassSurface>
    </div>
  );
}
